export const permissions = [
  "access",
  "create",
  "edit",
  "generate",
  "share",
  "finalize",
  "protocol",
] as const;
export type Permission = (typeof permissions)[number];
export type User = {
  id: string;
  name: string;
  phone: string;
  admin: boolean;
  active: boolean;
  permissions: Permission[];
};
export type Actor = User;
export type FileRecord = {
  id: string;
  name: string;
  mime: string;
  size: number;
  sha256: string;
  ownerId: string;
  officeId?: string;
  demandId?: string;
  purpose?: "demand";
  referenceId?: string;
  driveId?: string;
  extractedText?: string;
  extractionState: "ready" | "needs_review" | "unsupported" | "failed";
  extractionMessage?: string;
  createdAt: string;
};
export type OfficeStatus =
  | "draft"
  | "finalizing"
  | "finalized"
  | "queued"
  | "preparing"
  | "uploading"
  | "awaiting_signature"
  | "verifying"
  | "protocolled"
  | "recoverable_failure"
  | "awaiting_review";
export type HistoryItem = { at: string; text: string; actorId?: string };
export type Suggestion = {
  id: string;
  baseVersion: number;
  original: string;
  proposed: string;
  instruction: string;
  paragraph?: number;
  state: "pending" | "accepted" | "rejected";
  references: string[];
  createdAt: string;
  missing?: string[];
};
export type ProtocolAttempt = {
  id: string;
  confirmedBy: string;
  confirmedAt: string;
  officeVersion: number;
  files: { id: string; sha256: string; name: string; state: string }[];
  subject: string;
  recipientId: string;
  recipientName?: string;
  state: OfficeStatus;
  checkpoint?: string;
  signatureUrl?: string;
  error?: string;
  sendMayHaveOccurred?: boolean;
};
export type Office = {
  id: string;
  version: number;
  title: string;
  request: string;
  subject: string;
  recipientId: string;
  recipientName?: string;
  finalizedConfigVersion?: number;
  requesterName: string;
  requesterPhone: string;
  body: string;
  createdBy: string;
  sharedWith: string[];
  createdAt: string;
  updatedAt: string;
  status: OfficeStatus;
  attachmentIds: string[];
  /** Frozen at finalization. Missing on legacy documents means no embedded images. */
  pdfImageIds?: string[];
  number?: number;
  year?: number;
  finalizedAt?: string;
  pdfFileId?: string;
  solarNumber?: string;
  receiptFileId?: string;
  protocolledAt?: string;
  concludedBy?: string;
  previousOfficeId?: string;
  attempt?: ProtocolAttempt;
  suggestions: Suggestion[];
  history: HistoryItem[];
  followup: "none" | "awaiting_response" | "closed_with_response";
  responses: { id: string; at: string; text: string; fileIds: string[] }[];
  pendingEffects: string[];
  error?: string;
};
export type Reference = {
  id: string;
  name: string;
  category: string;
  recipientId?: string;
  description: string;
  origin: string;
  version: number;
  processingState: "ready" | "needs_review" | "failed";
  text: string;
  active: boolean;
  archived: boolean;
  preferred: boolean;
  tags: string[];
  fileId?: string;
  sourceOfficeId?: string;
  createdAt: string;
  updatedAt: string;
};
export type OfficeListItem = Pick<
  Office,
  | "id"
  | "title"
  | "subject"
  | "recipientId"
  | "recipientName"
  | "status"
  | "updatedAt"
  | "number"
  | "year"
  | "solarNumber"
  | "followup"
  | "pendingEffects"
>;
export type Job = {
  id: string;
  officeId: string;
  kind: "finalize" | "protocol" | "mirror" | "receipt" | "poll";
  state: "queued" | "running" | "done" | "failed" | "review";
  attempts: number;
  availableAt: string;
  leaseUntil?: string;
  error?: string;
  payload?: Record<string, unknown>;
};
export type Recipient = {
  id: string;
  name: string;
  description?: string;
  active: boolean;
};
export type Config = {
  identity: {
    name: string;
    logoFileId?: string;
    theme: "light" | "dark";
    colors: Record<string, string>;
  };
  ai: {
    enabled: boolean;
    model: string;
    monthlyBudgetCents: number;
    maxRequestCents: number;
    inputCentsPerMillion: number;
    outputCentsPerMillion: number;
    maxInputChars: number;
    maxOutputTokens: number;
    instructions: string;
    requiredFields: string[];
  };
  google: {
    enabled: boolean;
    spreadsheetId: string;
    annualSheetPattern: string;
    pdfFolderId: string;
    attachmentFolderId: string;
    receiptFolderId: string;
    referenceFolderId: string;
    mapping: Record<string, string>;
    expectedHeaders: Record<string, string>;
    externalNumberingConfirmed: boolean;
  };
  solar: { enabled: boolean; pollMinutes: number; workflowVerified: boolean };
  layout: {
    heading: string;
    footer: string;
    marginMm: number;
    fontSize: number;
    headerFileId?: string;
    footerFileId?: string;
    headerPlacement?: {
      xMm: number;
      topMm: number;
      widthMm: number;
      maxHeightMm: number;
    };
    footerPlacement?: {
      xMm: number;
      bottomMm: number;
      widthMm: number;
      maxHeightMm: number;
    };
    /** New drafts append image attachments after the letter, unless disabled. */
    appendImages?: boolean;
  };
  recipients: Recipient[];
  rewards: {
    points: number | null;
    xp: number | null;
    conclusionPolicy: "pending" | "confirming_operator";
  };
  limits: { maxUploadMb: number; maxFiles: number };
};
export type ConfigVersion = {
  id: string;
  version: number;
  config: Config;
  createdAt: string;
  publishedAt?: string;
  basedOn?: number;
};
export type AIInput = {
  request: string;
  body: string;
  instruction?: string;
  paragraph?: number;
  fields: Record<string, string>;
  references: { id: string; text: string }[];
  attachments: { name: string; text: string }[];
  config: Config["ai"];
};
export type AIOutput = {
  text: string;
  missing: string[];
  usage: { inputTokens: number; outputTokens: number };
  costCents: number;
};
export type SolarResult = {
  state: "protocolled" | "awaiting_signature" | "uncertain";
  solarNumber?: string;
  protocolledAt?: string;
  signatureUrl?: string;
  receipt?: { bytes: Uint8Array; mime: string; name: string };
  evidence: string;
};
export interface Ports {
  protocolReady?(): boolean;
  cloneFile?(
    id: string,
    ownerId: string,
    officeId: string,
  ): Promise<FileRecord>;
  generate(input: AIInput): Promise<AIOutput>;
  externalMaximum(year: number, config: Config): Promise<number>;
  finalize(office: Office, config: Config): Promise<{ pdfFileId: string }>;
  /** Validate actual composition before reserving an official number. No persistence. */
  validatePDF?(office: Office, config: Config): Promise<void>;
  preview?(office: Office, config: Config): Promise<Uint8Array>;
  mirror(office: Office, config: Config): Promise<void>;
  send(
    office: Office,
    config: Config,
    onCheckpoint: (state: OfficeStatus, checkpoint: string) => Promise<void>,
  ): Promise<SolarResult>;
  reconcile(office: Office, config: Config): Promise<SolarResult>;
  receipt?(office: Office, config: Config): Promise<string>;
  storeReceipt?(
    office: Office,
    receipt: { bytes: Uint8Array; mime: string; name: string },
    config: Config,
  ): Promise<string>;
  poll?(
    office: Office,
    config: Config,
  ): Promise<{ id: string; at: string; text: string; fileIds: string[] }[]>;
  fileText(id: string): Promise<{ name: string; text: string }>;
}
export class AppError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}
export const statusLabels: Record<OfficeStatus, string> = {
  draft: "Rascunho",
  finalizing: "Preparando PDF",
  finalized: "Finalizado · aguardando protocolo",
  queued: "Na fila",
  preparing: "Preparando envio",
  uploading: "Enviando arquivos",
  awaiting_signature: "Aguardando assinatura",
  verifying: "Verificando resultado",
  protocolled: "Protocolado",
  recoverable_failure: "Falha recuperável",
  awaiting_review: "Aguardando conferência",
};
